import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import type { Task } from '../types'
import { ActionTypeChip } from './ActionTypeChip'
import { AssigneeChip } from './AssigneeChip'
import { ClaimButton } from './ClaimButton'
import { ContextLink } from './ContextLink'
import { PriorityBadge } from './PriorityBadge'

interface KanbanCardProps {
  task: Task
  onClick?: (task: Task) => void
}

/**
 * Pojedyncza karta na boardzie. Sortable via @dnd-kit. id = `card-<taskId>`.
 * Pokazuje tytul, due/priorytet, assignee chip i przycisk claim/unclaim (dla grupowych).
 *
 * Klik bez ruchu (drag activationConstraint distance=4) wywoluje onClick - parent
 * KanbanBoard otwiera TaskDetailDrawer. ClaimButton ma stopPropagation zeby klik
 * w przycisk nie otwieral drawera.
 */
export function KanbanCard({ task, onClick }: KanbanCardProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: `card-${task.id}`, data: { type: 'card', taskId: task.id } })

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={() => onClick?.(task)}
      className="bg-white border border-slate-200 rounded p-2 mb-2 shadow-sm hover:shadow cursor-grab active:cursor-grabbing"
    >
      <div className="flex items-center gap-2 mb-1">
        <PriorityBadge priority={task.priority} />
        <span className="text-sm font-medium truncate flex-1">{task.title}</span>
      </div>
      {/* Linia: data + typ akcji (zawsze obok daty, niezaleznie od kontekstu).
          flex-wrap zeby dlugi typ akcji nie skracal sie tylko wskakiwal pod date. */}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500">
        {task.dueDate && <span>{task.dueDate}</span>}
        {task.actionTypeCode && task.actionTypeLabel && (
          <ActionTypeChip code={task.actionTypeCode} label={task.actionTypeLabel} />
        )}
      </div>
      {/* Linia: kontekst (umowa, lead) jesli przypisany */}
      {task.context && (
        <div className="mt-0.5 text-xs text-slate-500">
          <ContextLink context={task.context} stopPropagation />
        </div>
      )}
      {/* Linia: assignee zawsze w osobnej linii (klient prosil per [[feedback_frontend_granularity]]) */}
      <div className="mt-1 flex items-center gap-2 text-xs">
        <AssigneeChip assignee={task.assignee} claimedBy={task.claimedBy} />
      </div>
      <div className="mt-1" onClick={(e) => e.stopPropagation()}>
        <ClaimButton task={task} />
      </div>
    </div>
  )
}
